import { Injectable } from '@angular/core'
import { Observable, of, switchMap } from 'rxjs'
import { ConfirmationService } from 'primeng/api'
import { TranslateService } from '@ngx-translate/core'
import { BookingAllowanceShortfall, Booking } from '@app/models/booking.model'
import { BookingCallResult, BookingsService } from './bookings.service'

/**
 * "This booking goes past the group's credit pool — book it anyway?"
 *
 * PAPI refuses an overspend for EVERY caller, portal included, and only accepts it when the
 * request carries `override_allowance`. That is deliberate: the portal is trusted, but a silent
 * exemption would mean an admin could quietly blow through a client's quota without ever seeing a
 * number. So the portal asks, and the admin's answer is what unlocks it.
 *
 * The shape is attempt → refusal → confirm → retry, rather than a client-side prediction, because
 * the balance is only knowable under the row lock PAPI takes: a colleague booking at the same
 * moment can change the answer between reading it and acting on it. The first call IS the check.
 *
 * Both call sites (the create/edit form and the quick +30 extend) route through here so the rule
 * cannot drift apart between them.
 */
@Injectable({ providedIn: 'root' })
export class BookingAllowanceConfirmService {
  constructor(
    private confirmationService: ConfirmationService,
    private translate: TranslateService
  ) {}

  /**
   * Runs `attempt(false)`. If PAPI refuses it as an overspend, asks the admin and — only on accept
   * — re-runs it as `attempt(true)`.
   *
   * A declined dialog resolves to the ORIGINAL refusal, so callers see `success: false` and simply
   * do nothing, exactly as they would for any other refusal. Nothing is toasted here: the dialog
   * already said what happened, and a cancel is a choice, not an error.
   */
  runWithConfirmation(attempt: (overrideAllowance: boolean) => Observable<BookingCallResult<Booking>>): Observable<BookingCallResult<Booking>> {
    return attempt(false).pipe(
      switchMap((result) => {
        if (result.success || result.errorText !== BookingsService.ALLOWANCE_EXCEEDED) return of(result)

        return new Observable<BookingCallResult<Booking>>((observer) => {
          const emit = (value: BookingCallResult<Booking>) => {
            observer.next(value)
            observer.complete()
          }
          this.confirmationService.confirm({
            message: this.translate.instant('BOOKINGS.CONFIRM.ALLOWANCE_EXCEEDED', this.shortfallParams(result)),
            header: this.translate.instant('BOOKINGS.CONFIRM.ALLOWANCE_EXCEEDED_HEADER'),
            icon: 'pi pi-exclamation-triangle',
            accept: () => attempt(true).subscribe(emit),
            reject: () => emit(result),
          })
        })
      })
    )
  }

  /**
   * Numbers for the dialog. `data` rides on the refusal itself, so the admin is told how far over
   * they are rather than just that they are over; if it is ever missing the sentence still reads,
   * with em dashes where the figures would be.
   */
  private shortfallParams(result: BookingCallResult<Booking>): { requested: string; remaining: string; over: string } {
    const shortfall = result.data as unknown as BookingAllowanceShortfall | undefined
    if (!shortfall || shortfall.credits_requested == null || shortfall.credits_remaining == null) {
      return { requested: '—', remaining: '—', over: '—' }
    }
    const over = Math.round((Number(shortfall.credits_requested) - Number(shortfall.credits_remaining)) * 100) / 100
    return { requested: String(shortfall.credits_requested), remaining: String(shortfall.credits_remaining), over: String(over) }
  }
}
